<script setup lang="ts">
import { storeToRefs } from 'pinia'

import InteractionCard from './interaction/Card.vue'
import History from './interaction/History.vue'

import { useMiscResizeStore } from '~/stores/miscResize'
import { useUserStore } from '~/stores/user'
import { useUserBandStore } from '~/stores/userBand'

import { Breakpoints } from '~/enums/breakpoints'

import type { Influencer } from '~/types/influencer'
import type InfluencerInteraction from '~/types/influencerInteraction'

type Props = {
  influencer: Influencer
  interactions: InfluencerInteraction[]
}

const props = defineProps<Props>()

const { BAND_DATA } = storeToRefs(useUserBandStore())
const { IS_AGENCY } = storeToRefs(useUserStore())
const { SCREEN_WIDTH } = storeToRefs(useMiscResizeStore())

const selectedBandInteractions = ref<InfluencerInteraction[]>([])
const otherBandsInteractions = ref<InfluencerInteraction[]>([])

const bandName = computed<string>(() => BAND_DATA.value?.name ?? '')

onMounted(() => {
  if (IS_AGENCY.value) computeArrays()
  else selectedBandInteractions.value = [...props.interactions]
})

function computeArrays(): void {
  selectedBandInteractions.value = props.interactions.filter(
    (i) => i.band.slug === BAND_DATA.value?.slug,
  )

  otherBandsInteractions.value = props.interactions.filter(
    (i) => i.band.slug !== BAND_DATA.value?.slug,
  )
}
</script>

<template>
  <div class="influencerProfileBodyMainWrapper mainParentContainer">
    <div v-if="SCREEN_WIDTH >= Breakpoints.lg">
      <div class="splitScreen">
        <div class="content">
          <div
            :class="{
              bottomMargin: IS_AGENCY && selectedBandInteractions.length,
            }"
          >
            <div
              v-if="IS_AGENCY && selectedBandInteractions.length"
              class="titleBlock"
            >
              <span class="titleLine">
                <span class="title">{{
                  $t('influencer.profile.slug.interaction.interaction00')
                }}</span>
                <span class="title">{{ bandName }}</span>
                <span class="title">{{ $t('common.and') }}</span>
                <span class="title">{{ influencer.entity }}</span>
              </span>
              <span class="subtitle">{{
                $t('influencer.profile.slug.interaction.interaction01')
              }}</span>
            </div>
            <InteractionCard
              v-for="(interaction, index) in selectedBandInteractions"
              :key="interaction.band.id + index"
              :interaction="interaction"
              :influencer="influencer"
              class="interactionCard"
            />
          </div>
          <div v-if="IS_AGENCY && otherBandsInteractions.length">
            <div class="titleBlock">
              <span class="titleLine">
                <span class="title">{{
                  $t('influencer.profile.slug.interaction.interaction02')
                }}</span>
                <span class="title">{{ influencer.entity }}</span>
              </span>
              <span class="subtitle">{{
                $t('influencer.profile.slug.interaction.interaction03')
              }}</span>
            </div>
            <InteractionCard
              v-for="(interaction, index) in otherBandsInteractions"
              :key="interaction.band.id + index"
              :interaction="interaction"
              :influencer="influencer"
              class="interactionCard"
            />
          </div>
        </div>
        <div class="infosWrapper">
          <History :influencer="influencer" :interactions="interactions" />
        </div>
      </div>
    </div>
    <div v-else class="fullScreen">
      <div v-if="SCREEN_WIDTH < Breakpoints.lg" class="infosWrapper">
        <History :influencer="influencer" :interactions="interactions" />
      </div>
      <div class="content">
        <div
          :class="{
            bottomMargin: IS_AGENCY && selectedBandInteractions.length,
          }"
        >
          <div
            v-if="IS_AGENCY && selectedBandInteractions.length"
            class="titleBlock"
          >
            <span class="titleLine">
              <span class="title">{{
                $t('influencer.profile.slug.interaction.interaction00')
              }}</span>
              <span class="title">{{ bandName }}</span>
              <span class="title">{{ $t('common.and') }}</span>
              <span class="title">{{ influencer.entity }}</span>
            </span>
            <span class="subtitle">{{
              $t('influencer.profile.slug.interaction.interaction01')
            }}</span>
          </div>
          <InteractionCard
            v-for="(interaction, index) in selectedBandInteractions"
            :key="interaction.band.id + index"
            :interaction="interaction"
            :influencer="influencer"
            class="interactionCard"
          />
        </div>
        <div v-if="IS_AGENCY && otherBandsInteractions.length">
          <div class="titleBlock bis">
            <span class="titleLine">
              <span class="title">{{
                $t('influencer.profile.slug.interaction.interaction02')
              }}</span>
              <span class="title">{{ influencer.entity }}</span>
            </span>
            <span class="subtitle">{{
              $t('influencer.profile.slug.interaction.interaction03')
            }}</span>
          </div>
          <InteractionCard
            v-for="(interaction, index) in otherBandsInteractions"
            :key="interaction.band.id + index"
            :interaction="interaction"
            :influencer="influencer"
            class="interactionCard"
          />
        </div>
      </div>
    </div>
  </div>
</template>

<style scoped lang="scss">
.splitScreen {
  @media screen and (min-width: 1024px) {
    display: grid;
    grid-template-columns: repeat(12, 1fr);
    grid-gap: var(--grid-gap);
  }
}
.influencerProfileBodyMainWrapper {
  position: relative;
  width: 100%;
  margin-top: 24px;
  margin-bottom: 100px;

  @media screen and (min-width: 768px) {
    margin-top: 40px;
  }
}

.fullScreen {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;
}

.content {
  position: relative;
  width: 100%;
  margin-top: 48px;
  @media screen and (min-width: 768px) {
    margin-top: 80px;
  }
  @media screen and (min-width: 1024px) {
    margin-top: 0px;
    grid-column: span 8;
    margin-top: unset;
  }
}

.infosWrapper {
  position: relative;
  width: 100%;
  display: flex;
  flex-direction: column;

  @media screen and (min-width: 1024px) {
    grid-column: 10 / span 3;
    position: sticky;
    top: 24px;
    height: min-content;
  }
}

.interactionCard:not(:last-child) {
  margin-bottom: 16px;

  @media screen and (min-width: 768px) {
    margin-bottom: 24px;
  }
}

.titleBlock {
  display: flex;
  flex-direction: column;
}

.bottomMargin {
  margin-bottom: 40px;
  @media screen and (min-width: 1024px) {
    margin-bottom: 80px;
  }
}

.title {
  color: var(--color-black);
  font-size: var(--font-size-title);
  font-weight: 600;
}

.subtitle {
  color: var(--color-black);
  font-size: var(--font-size-big);
  margin-top: 8px;
  margin-bottom: 24px;
}
</style>
